import type { QueryClient } from '@tanstack/react-query';

import { clearIdentityScopedSessionMemories } from './identity-scoped-session-memory';

/**
 * Purges the shared TanStack cache whenever the confirmed identity changes.
 *
 * Every cached entry is treated as identity-scoped, not only `convexQuery`
 * keys: custom keys such as signed file URLs and folder previews are keyed by
 * workspace rather than identity, so keeping them would let the next account
 * read the previous account's results.
 */
export type ConvexQueryAuthLifecycle = ((
  identity: string | null
) => Promise<void>) & {
  /**
   * Starts the lifecycle at the identity the server rendered for (from the
   * request's own token), so its hydrated cache survives the client's startup
   * `null → user` confirmation. Until the first real identity is confirmed,
   * pending `null` updates keep the seed; confirming a different identity
   * still purges everything. Only honored before any transition.
   */
  seedServerIdentity: (identity: string) => void;
};

export function createConvexQueryAuthLifecycle(
  queryClient: Pick<QueryClient, 'cancelQueries' | 'removeQueries'>
): ConvexQueryAuthLifecycle {
  let previousIdentity: string | null | undefined;
  let awaitingSeedConfirmation = false;
  let transitionQueue = Promise.resolve();

  const update = (identity: string | null) => {
    const transition = transitionQueue.then(async () => {
      if (awaitingSeedConfirmation) {
        if (identity === null) return;
        awaitingSeedConfirmation = false;
      }
      if (previousIdentity === undefined) {
        previousIdentity = identity;
        return;
      }
      if (previousIdentity === identity) return;

      // Session memories of VFS data belong to the previous principal.
      clearIdentityScopedSessionMemories();

      // Cancel before removing so an in-flight response for the previous
      // identity cannot repopulate the cache after the switch.
      await queryClient.cancelQueries();
      queryClient.removeQueries();
      previousIdentity = identity;
    });
    transitionQueue = transition.catch(() => undefined);
    return transition;
  };

  return Object.assign(update, {
    seedServerIdentity(identity: string) {
      if (previousIdentity !== undefined) return;
      previousIdentity = identity;
      awaitingSeedConfirmation = true;
    },
  });
}
